import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import SiteHeader from "@/components/SiteHeader";
import SiteFooter from "@/components/SiteFooter";
import VehicleCard from "@/components/VehicleCard";
import AgencyLodgingsStrip from "@/components/AgencyLodgingsStrip";
import SearchWidget from "@/components/SearchWidget";
import { api } from "@/lib/api";
import { MapPin, Filter, Star } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

export default function Search() {
  const [params] = useSearchParams();
  const city = params.get("city") || "";
  const withDriverParam = params.get("with_driver"); // "true" | "false" | null
  const rentalType = params.get("rental_type") || "";
  const [vehicles, setVehicles] = useState([]);
  const [category, setCategory] = useState("all");
  const [transmission, setTransmission] = useState("all");
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    const q = {};
    if (city) q.city = city;
    if (withDriverParam === "true") q.with_driver = true;
    if (withDriverParam === "false") q.with_driver = false;
    if (rentalType) q.rental_type = rentalType;
    if (category !== "all") q.category = category;
    if (transmission !== "all") q.transmission = transmission;
    api.get("/vehicles", { params: q })
      .then((r) => setVehicles(r.data))
      .finally(() => setLoading(false));
  }, [city, withDriverParam, rentalType, category, transmission]);

  // Group by agency
  const grouped = vehicles.reduce((acc, v) => {
    const key = v.agency?.id || "unknown";
    if (!acc[key]) acc[key] = { agency: v.agency, vehicles: [] };
    acc[key].vehicles.push(v);
    return acc;
  }, {});

  return (
    <div className="min-h-screen bg-[#f8f9fa]">
      <SiteHeader />
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-8">
        <div className="mb-8">
          <SearchWidget />
        </div>

        <div className="flex flex-wrap items-center justify-between gap-4 mb-6">
          <div>
            <h1 className="font-display text-2xl md:text-3xl font-black text-[#0a1e3f]">
              {city ? `Véhicules disponibles à ${city}` : "Tous les véhicules"}
            </h1>
            <p className="text-sm text-gray-500 mt-1">{vehicles.length} véhicules · {Object.keys(grouped).length} agences</p>
            <div className="flex flex-wrap gap-2 mt-2" data-testid="search-active-filters">
              {withDriverParam === "true" && <span className="text-[11px] font-bold bg-[#0a1e3f] text-white px-2.5 py-0.5 rounded-full">Avec chauffeur</span>}
              {withDriverParam === "false" && <span className="text-[11px] font-bold bg-gray-100 text-[#0a1e3f] px-2.5 py-0.5 rounded-full">Sans chauffeur</span>}
              {rentalType === "day" && <span className="text-[11px] font-bold bg-[#dcfce7] text-[#0e7a3f] px-2.5 py-0.5 rounded-full">Location au jour</span>}
              {rentalType === "week" && <span className="text-[11px] font-bold bg-[#dcfce7] text-[#0e7a3f] px-2.5 py-0.5 rounded-full">À la semaine</span>}
              {rentalType === "month" && <span className="text-[11px] font-bold bg-[#dcfce7] text-[#0e7a3f] px-2.5 py-0.5 rounded-full">Au mois</span>}
            </div>
          </div>
          <div className="flex items-center gap-3">
            <div className="flex items-center gap-2 text-sm text-gray-500"><Filter className="h-4 w-4" /> Filtres</div>
            <Select value={category} onValueChange={setCategory}>
              <SelectTrigger data-testid="filter-category" className="w-40 h-10 rounded-full"><SelectValue placeholder="Catégorie" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Toutes catégories</SelectItem>
                <SelectItem value="SUV">SUV</SelectItem>
                <SelectItem value="Berline">Berline</SelectItem>
                <SelectItem value="Pick-up">Pick-up</SelectItem>
                <SelectItem value="Minibus">Minibus</SelectItem>
                <SelectItem value="4x4">4x4</SelectItem>
              </SelectContent>
            </Select>
            <Select value={transmission} onValueChange={setTransmission}>
              <SelectTrigger data-testid="filter-transmission" className="w-40 h-10 rounded-full"><SelectValue placeholder="Boîte" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Toutes boîtes</SelectItem>
                <SelectItem value="Automatique">Automatique</SelectItem>
                <SelectItem value="Manuelle">Manuelle</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>

        {loading ? (
          <div className="text-center py-24 text-gray-500">Chargement…</div>
        ) : vehicles.length === 0 ? (
          <div className="bg-white rounded-2xl border border-dashed border-gray-200 p-16 text-center">
            <p className="text-gray-600 font-semibold">Aucun véhicule trouvé{city ? ` à ${city}` : ""}.</p>
            <p className="text-sm text-gray-400 mt-2">Essayez une autre ville ou modifiez vos filtres.</p>
          </div>
        ) : (
          <div className="space-y-10">
            {Object.values(grouped).map(({ agency, vehicles }) => (
              <section key={agency?.id || "n"}>
                <div className="flex items-center gap-3 mb-4">
                  {agency?.logo_url ? (
                    <img src={agency.logo_url} className="h-10 w-10 rounded-full object-cover" alt={agency.name} />
                  ) : (
                    <div className="h-10 w-10 rounded-full bg-[#0a1e3f] text-[#d4a437] font-black flex items-center justify-center">
                      {agency?.name?.[0] || "M"}
                    </div>
                  )}
                  <div>
                    <div className="font-display text-xl font-bold text-[#0a1e3f]">{agency?.name || "Agence"}</div>
                    <div className="text-xs text-gray-500 flex items-center gap-2">
                      <span className="flex items-center gap-1"><MapPin className="h-3 w-3" /> {agency?.city}, {agency?.country}</span>
                      {agency?.rating > 0 && (
                        <span className="inline-flex items-center gap-0.5 text-[#0a1e3f] font-bold" data-testid={`agency-rating-${agency.id}`}>
                          <Star className="h-3 w-3 fill-[#d4a437] text-[#d4a437]" />
                          {Number(agency.rating).toFixed(1)}
                          <span className="text-gray-400 font-normal">({agency.reviews_count || 0})</span>
                        </span>
                      )}
                    </div>
                  </div>
                  <div className="ml-auto text-xs font-semibold text-[#0e7a3f] bg-[#dcfce7] px-3 py-1 rounded-full">{vehicles.length} véhicule{vehicles.length > 1 ? "s" : ""}</div>
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
                  {vehicles.map((v) => <VehicleCard key={v.id} v={v} />)}
                </div>
                <AgencyLodgingsStrip agencyId={agency?.id} />
              </section>
            ))}
          </div>
        )}
      </div>
      <SiteFooter />
    </div>
  );
}
